import React from 'react';
import { Trophy, Flame, CheckCircle, Target, Heart, Shield, Star } from 'lucide-react';

const AchievementsPanel = ({ userState, taskStats30d }) => {
  const streak = userState?.streak || 0;
  const badges = userState?.badges || [];

  const colorMap = {
    amber: { bg: 'rgba(245,158,11,0.08)', border: 'rgba(245,158,11,0.2)', text: '#FBBF24', glow: 'rgba(245,158,11,0.15)' },
    orange: { bg: 'rgba(234,88,12,0.08)', border: 'rgba(234,88,12,0.2)', text: '#FB923C', glow: 'rgba(234,88,12,0.15)' },
    gold: { bg: 'rgba(245,158,11,0.12)', border: 'rgba(245,158,11,0.35)', text: '#FCD34D', glow: 'rgba(245,158,11,0.25)' },
    emerald: { bg: 'rgba(52,211,153,0.08)', border: 'rgba(52,211,153,0.2)', text: '#34D399', glow: 'rgba(52,211,153,0.15)' },
    cyan: { bg: 'rgba(56,189,248,0.08)', border: 'rgba(56,189,248,0.2)', text: '#67E8F9', glow: 'rgba(56,189,248,0.15)' },
    purple: { bg: 'rgba(168,85,247,0.08)', border: 'rgba(168,85,247,0.2)', text: '#C084FC', glow: 'rgba(168,85,247,0.15)' },
    pink: { bg: 'rgba(236,72,153,0.08)', border: 'rgba(236,72,153,0.2)', text: '#F472B6', glow: 'rgba(236,72,153,0.15)' },
    rose: { bg: 'rgba(244,63,94,0.08)', border: 'rgba(244,63,94,0.2)', text: '#FB7185', glow: 'rgba(244,63,94,0.15)' },
    blue: { bg: 'rgba(59,130,246,0.08)', border: 'rgba(59,130,246,0.2)', text: '#60A5FA', glow: 'rgba(59,130,246,0.15)' },
  };

  const IconMap = { flame: Flame, check: CheckCircle, target: Target, trophy: Trophy, heart: Heart, shield: Shield, star: Star };

  return (
    <div className="glass-card relative rounded-[20px] overflow-hidden" style={{ padding: 0 }} data-testid="achievements-card">
      <div className="absolute inset-0 pointer-events-none" style={{
        background: 'radial-gradient(ellipse at 15% 10%, rgba(245,158,11,0.06) 0%, transparent 45%)',
      }} />

      <div className="relative z-10 p-5">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{
              background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.2)',
              boxShadow: '0 0 12px rgba(245,158,11,0.15)',
            }}>
              <Trophy className="w-4 h-4" style={{ color: '#FBBF24' }} />
            </div>
            <h3 style={{ color: '#F8FAFC', fontWeight: 700, fontSize: '18px', letterSpacing: '-0.01em' }}>Başarıların</h3>
          </div>
        </div>

        {/* Streak Hero */}
        <div className="relative rounded-[16px] p-4 mb-4 flex items-center gap-4 overflow-hidden" style={{
          background: 'linear-gradient(135deg, rgba(245,158,11,0.08), rgba(234,88,12,0.04))',
          border: `1px solid rgba(245,158,11,${streak >= 7 ? '0.35' : '0.15'})`,
          boxShadow: streak >= 7
            ? '0 0 20px rgba(245,158,11,0.2), inset 0 0 20px rgba(245,158,11,0.05)'
            : 'inset 0 0 15px rgba(245,158,11,0.03)',
        }} data-testid="streak-hero">
          <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse at 20% 40%, rgba(245,158,11,0.10), transparent 60%)' }} />
          <div className="relative w-14 h-14 rounded-full flex items-center justify-center" style={{
            background: 'rgba(245,158,11,0.15)', border: '1px solid rgba(245,158,11,0.30)',
            boxShadow: '0 0 18px rgba(245,158,11,0.25), inset 0 0 10px rgba(245,158,11,0.08)',
          }}>
            <Flame className="w-7 h-7" style={{ color: '#FBBF24', filter: 'drop-shadow(0 0 8px rgba(245,158,11,0.8)) drop-shadow(0 0 16px rgba(245,158,11,0.4))' }} />
          </div>
          <div className="relative flex-1">
            <p style={{ color: '#FBBF24', fontSize: '28px', fontWeight: 800, lineHeight: 1 }}>{streak}</p>
            <p style={{ color: '#FDE68A', fontSize: '13px', fontWeight: 600, marginTop: '2px' }}>Gün Ardışık Seri</p>
            <p style={{ color: '#AAB4C5', fontSize: '12px', marginTop: '2px' }}>
              {streak === 0 && 'Bugün ilk adımı at!'}
              {streak >= 1 && streak < 3 && 'İyi başlangıç, devam et!'}
              {streak >= 3 && streak < 7 && 'Harika gidiyorsun!'}
              {streak >= 7 && streak < 14 && 'Bir hafta! Muhteşem!'}
              {streak >= 14 && streak < 30 && 'İki hafta! Sen bir kahraman!'}
              {streak >= 30 && 'Bir ay! İnanılmaz başarı!'}
            </p>
          </div>
          <div className="relative flex flex-col items-center gap-1">
            {[3, 7, 14, 30].map(m => (
              <div key={m} className="w-6 h-1.5 rounded-full" style={{
                background: streak >= m ? 'linear-gradient(90deg, #F59E0B, #FBBF24)' : 'rgba(255,255,255,0.06)',
                boxShadow: streak >= m ? '0 0 6px rgba(245,158,11,0.4)' : 'none',
              }} />
            ))}
          </div>
        </div>

        {/* Badge Grid */}
        {badges.length > 0 ? (
          <div className="space-y-2" data-testid="badge-list">
            {badges.slice(0, 4).map((badge) => {
              const c = colorMap[badge.color] || colorMap.amber;
              const BadgeIcon = IconMap[badge.icon] || Star;
              return (
                <div key={badge.id} className="flex items-center gap-3 p-3 rounded-xl transition-all duration-200 hover:scale-[1.01]" style={{
                  background: c.bg, border: `1px solid ${c.border}`,
                  boxShadow: `inset 0 0 15px ${c.glow}, 0 0 10px ${c.glow}`,
                }} data-testid={`badge-${badge.id}`}>
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{
                    background: c.bg, border: `1px solid ${c.border}`,
                  }}>
                    <BadgeIcon className="w-4 h-4" style={{ color: c.text, filter: `drop-shadow(0 0 6px ${c.glow})` }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold truncate" style={{ color: c.text }}>{badge.label}</p>
                    <p className="text-[11px] truncate" style={{ color: '#94A3B8' }}>{badge.desc}</p>
                  </div>
                  {badge.tier >= 3 && (
                    <Star className="w-4 h-4 shrink-0" style={{ color: '#FBBF24', filter: 'drop-shadow(0 0 4px rgba(245,158,11,0.5))' }} />
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-3" style={{ color: '#64748B', fontSize: '13px' }}>
            <p>Henuz rozet kazanmadin.</p>
            <p className="mt-1" style={{ color: '#94A3B8', fontSize: '12px' }}>Görevlerini yaparak rozetlerini topla!</p>
          </div>
        )}

        {/* Quick stats row */}
        <div className="grid grid-cols-3 gap-2 mt-4">
          <div className="text-center p-2 rounded-lg" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.04)' }}>
            <p className="text-lg font-bold" style={{ color: '#FBBF24' }}>{taskStats30d.completed || 0}</p>
            <p className="text-[10px]" style={{ color: '#94A3B8' }}>Görev</p>
          </div>
          <div className="text-center p-2 rounded-lg" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.04)' }}>
            <p className="text-lg font-bold" style={{ color: '#67E8F9' }}>{userState?.data_sources?.test_count || 0}</p>
            <p className="text-[10px]" style={{ color: '#94A3B8' }}>Test</p>
          </div>
          <div className="text-center p-2 rounded-lg" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.04)' }}>
            <p className="text-lg font-bold" style={{ color: '#C084FC' }}>{badges.length}</p>
            <p className="text-[10px]" style={{ color: '#94A3B8' }}>Rozet</p>
          </div>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-[1px]" style={{
        background: 'linear-gradient(90deg, transparent, rgba(168,85,247,0.25), rgba(56,189,248,0.2), transparent)',
        boxShadow: '0 0 5px rgba(168,85,247,0.15)',
      }} />
    </div>
  );
};

export default AchievementsPanel;
